import { useEffect } from 'react'
import { useQuery } from 'react-query'
import { useAuth } from 'context/JWTContext'
import { UserApi } from 'api/api'
import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { useGameRoundsStore, useSelectedFullRoundStore, useSelectedRoundStore } from '@/store/store'
import { RoundWithUserData } from 'types/types'

const POLL_INTERVAL = 60 * 1000

// Stop polling once every round is done - no round left that can still change status.
const hasRoundInFlight = (rounds?: RoundWithUserData[]) =>
  !rounds?.length || rounds.some((r) => r.status === 'ACTIVE' || r.status === 'UPCOMING')

export const useGetGameRounds = (gameId: number, enabled: boolean) => {
  const { isAuthenticated } = useAuth()
  const [setRounds] = useGameRoundsStore((state) => [state.setRounds])
  const [setSelectedFullRound] = useSelectedFullRoundStore((state) => [state.setSelectedRound])
  const [currentSimpleRound] = useSelectedRoundStore((state) => [state.selectedRound])

  const query = useQuery({
    queryKey: [QUERY_KEYS.GAME_ROUNDS, gameId],
    queryFn: () => UserApi.getRoundsByGame(gameId, isAuthenticated),
    enabled: enabled && !!gameId,
    staleTime: POLL_INTERVAL,
    refetchInterval: (data) => (hasRoundInFlight(data) ? POLL_INTERVAL : false),
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  })

  useEffect(() => {
    if (!enabled || !gameId) {
      setRounds(0, [])
      return
    }
    setRounds(gameId, query.data ?? [])
  }, [enabled, gameId, query.data, setRounds])

  // Clear stale selectedFullRound immediately when switching games so consumers
  // don't read the previous game's round during the new fetch.
  useEffect(() => {
    if (enabled && gameId) {
      setSelectedFullRound(null)
    }
  }, [gameId])

  useEffect(() => {
    if (!query.data?.length) return
    if (!currentSimpleRound) {
      const fallback =
        query.data.find((r) => r.status === 'ACTIVE') ??
        query.data.find((r) => r.status === 'UPCOMING') ??
        query.data[0]
      if (fallback) setSelectedFullRound(fallback)
      return
    }
    const round = query.data.find((r) => r.id === currentSimpleRound.id)
    if (round) setSelectedFullRound(round)
  }, [currentSimpleRound?.id, query.data])

  return query
}
